/* eslint-disable indent */
import { Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { GlobalServiceMessage, SupportChat, SupportChatHelpButton } from '@/components/common';
import { PWAInstallPrompt } from '@/components/common/PWAInstallPrompt';
import { TABLET_SCREEN_SIZE } from '@/constants';
import { AuthContext, OfflineContext, ProfilePictureContext } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { getPreviousRoute, hasPreviousPage, isRouteOfflineAllowed } from '@/helpers';
import { useCanUseSupportChat } from '@/hooks';
import { Sidebar } from '@/layout';
import { SIDEBAR_LINK_BUTTON_STYLE } from '@/layout/Sidebar/Sidebar.styles';
import { UserAvatar } from '@/pages/ProfilePage/UserAvatar';
import { CalendarLeftArrow as BackIcon, HamburgerMenu, LogOut, SentoaLogo } from '@/resources';
import { TestIds } from '@/shared/test-ids';

export const Layout = () => {
  const navigate = useNavigate();
  const [t] = useTranslation('common');
  const pathName = useLocation().pathname;
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isTabletView, setIsTabletView] = useState<boolean>(false);

  const { currentUserData, permissions, logout } = useContext(AuthContext);
  const { profilePictureUrl } = useContext(ProfilePictureContext);
  const { isOnline } = useContext(OfflineContext);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const canViewText = permissions.includes(PERMISSION_TYPE.VIEW_TEXT);
  const canControl =
    permissions.includes(PERMISSION_TYPE.CONTROL_ASSETS) &&
    permissions.includes(PERMISSION_TYPE.CONTROL_STREAM);
  const canUseSupportChat = useCanUseSupportChat();

  useEffect(() => {
    const handleResize = () => {
      setIsTabletView(window.innerWidth < TABLET_SCREEN_SIZE);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const match = matchRoutes(
    [
      {
        path: '/events/:eventId/upload-content'
      },
      {
        path: '/'
      }
    ],
    location
  );
  const handleRouteChange = () => {
    let newRoute = getPreviousRoute(pathName);
    if (!hasOrgAccess && !(canViewText || canControl)) {
      newRoute = '/events/:eventId/upload-content';
    }
    navigate(newRoute as string);
  };

  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    }
  });

  //Offline gate: redirect disallowed routes to /playlists
  if (!isOnline && !isRouteOfflineAllowed(pathName)) {
    return <Navigate to={PRIVATE_PAGE_ROUTES.PLAYLISTS} replace />;
  }

  return (
    <>
      <GlobalServiceMessage />
      {((isTabletView && !pathName.includes('upload-content') && hasOrgAccess && !token) ||
        (canViewText && !hasOrgAccess && !pathName.includes('profile')) ||
        (canControl && !hasOrgAccess && !pathName.includes('profile'))) && (
        <div className='relative flex h-[45px] max-w-full items-center justify-between bg-primary-gray px-3 text-2xl'>
          {hasPreviousPage(pathName) ? (
            <BackIcon
              data-testid={TestIds.layout.backButton}
              className='absolute left-3 top-1/2 -translate-y-1/2 cursor-pointer text-lg text-primary-blue'
              onClick={handleRouteChange}
            />
          ) : hasOrgAccess ? (
            <HamburgerMenu
              data-testid={TestIds.layout.hamburgerButton}
              className='absolute left-3 top-1/2 -translate-y-1/2'
              onClick={() => setIsSidebarOpen(true)}
            />
          ) : null}
          <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
            <SentoaLogo className='h-[26px] w-[108px]' />
          </div>
          {((canViewText && !hasOrgAccess) || (canControl && !hasOrgAccess)) && (
            <div className='ml-auto flex items-center'>
              {canUseSupportChat && <SupportChatHelpButton className='mr-4 md:hidden' />}
              <div
                data-testid={TestIds.layout.profileButtonDesktop}
                className={`hidden md:flex ${SIDEBAR_LINK_BUTTON_STYLE} ${window.location.pathname.startsWith('/profile') ? 'border-r-4 border-r-primary-blue !font-bold !text-primary-blue' : ''} md:mr-2`}
                onClick={() => navigate('/profile')}>
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt={t('profilePicture')}
                    className='mr-1 size-7 rounded-full object-cover'
                  />
                ) : (
                  <UserAvatar style='w-7 h-7 text-sm bg-primary-white border border-accent-light-gray text-primary-blue' />
                )}
                <div className='flex flex-col'>
                  <div className='ml-1 max-w-[140px] overflow-hidden truncate'>
                    {currentUserData?.firstName + ' ' + currentUserData?.lastName}
                  </div>
                  {currentUserData?.role && (
                    <div className='ml-1 text-xs !font-normal !text-secondary-steel-blue'>
                      {t(currentUserData?.role.name)}
                    </div>
                  )}
                </div>
              </div>
              <div
                data-testid={TestIds.layout.profileButtonMobile}
                className='mr-4 block cursor-pointer md:hidden'
                onClick={() => navigate('/profile')}>
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt={t('profilePicture')}
                    className='size-7 rounded-full object-cover'
                  />
                ) : (
                  <UserAvatar style='w-7 h-7 text-sm bg-primary-white border border-accent-light-gray text-primary-blue' />
                )}
              </div>
              <span data-testid={TestIds.layout.logoutButton} className='mr-3 flex'>
                <LogOut className='cursor-pointer text-2xl text-primary-blue' onClick={logout} />
              </span>
            </div>
          )}
        </div>
      )}
      <div className='relative flex w-full'>
        {match && (
          <Suspense>
            <Outlet />
          </Suspense>
        )}
        {!match && (
          <>
            {isTabletView ? (
              <div
                className={`absolute top-[-45px] z-50 transition-transform duration-150 ${
                  isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}>
                <Sidebar isTabletView={isTabletView} closeSidebar={() => setIsSidebarOpen(false)} />
                <div
                  onClick={() => setIsSidebarOpen(false)}
                  className='h-screen w-screen backdrop-blur-[1.5px]'
                />
              </div>
            ) : (
              ((currentUserData && currentUserData.events && currentUserData.events.length > 1) ||
                hasOrgAccess) &&
              !pathName.includes('stream') && <Sidebar />
            )}
            <div className='flex w-full flex-col justify-start overflow-hidden'>
              <Suspense>
                <Outlet />
              </Suspense>
            </div>
          </>
        )}
      </div>
      {!pathName.includes('upload-content') && pathName.includes('profile') && !token && (
        <div className='sticky bottom-0 mb-0 h-[18px] w-full bg-secondary-steel-blue text-center text-2xs text-primary-white md:hidden'>
          {`${t('copyrights')} © Sentoa ${new Date().getFullYear()}`}
        </div>
      )}
      <PWAInstallPrompt />
      {canUseSupportChat && <SupportChat />}
    </>
  );
};
